import { useState, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Network,
  Map,
  Target,
  Users,
  ChevronDown,
  ChevronRight,
  Globe,
  PanelLeftClose,
  PanelLeftOpen,
  ShieldAlert,
  Shield,
  Monitor,
  Upload,
  BookOpen,
  Settings,
  Search,
  Zap,
} from 'lucide-react';
import { useAuth } from '../../auth/AuthProvider';
import useStore from '../../store/useStore';
import { PATH_PERMISSION } from '../../config/permissions';
import OrgSwitcher from './OrgSwitcher';

const menuSections = [
  {
    id: 1,
    title: 'General',
    items: [
      { path: '/', icon: LayoutDashboard, label: 'Dashboard' },
    ],
  },
  {
    id: 2,
    title: 'Assets & Defense',
    items: [
      { path: '/network-map', icon: Network, label: 'Network Map' },
      { path: '/assets', icon: Map, label: 'My Assets' },
      { path: '/agents', icon: Monitor, label: 'Agent Registry' },
      { path: '/import', icon: Upload, label: 'Import Assets' },
      { path: '/baseline', icon: Target, label: 'Baseline Management' },
    ],
  },
  {
    id: 3,
    title: 'Intelligence',
    items: [
      { path: '/security-alerts', icon: ShieldAlert, label: 'Security Alerts' },
      { path: '/findings', icon: Search, label: 'Findings' },
      { path: '/response-actions', icon: Zap, label: 'Response Actions' },
      { path: '/cases', icon: Shield, label: 'Cases' },
      { path: '/playbooks', icon: BookOpen, label: 'Playbooks' },
    ],
  },
  {
    id: 4,
    title: 'Management',
    items: [
      { path: '/org/members', icon: Users, label: 'Members & Roles' },
      { path: '/settings', icon: Settings, label: 'Settings' },
    ],
  },
];

const STORAGE_KEY_PREFIX = 'sidebar_section_';

const loadCollapseState = (sectionId) => {
  try {
    const stored = localStorage.getItem(`${STORAGE_KEY_PREFIX}${sectionId}`);
    return stored === 'true';
  } catch {
    return false;
  }
};

const saveCollapseState = (sectionId, collapsed) => {
  try {
    localStorage.setItem(`${STORAGE_KEY_PREFIX}${sectionId}`, String(collapsed));
  } catch {
    // localStorage unavailable — no-op
  }
};

const NavLink = ({ to, icon: Icon, label, isActive, disabled, minimized }) => (
  <div className="relative group">
    <Link
      to={disabled ? '#' : to}
      onClick={e => { if (disabled) e.preventDefault(); }}
      aria-current={isActive ? 'page' : undefined}
      className={`relative flex items-center h-10 rounded-lg text-sm transition-colors ${minimized ? 'justify-center px-0' : 'gap-3 px-3'} ${
        isActive ? 'font-medium' : disabled ? 'opacity-40 cursor-not-allowed' : ''
      }`}
      style={{
        background: isActive ? 'var(--surf-raised)' : 'transparent',
        color: isActive ? 'var(--ink-text)' : disabled ? 'var(--ink-faint)' : 'var(--ink-muted)',
      }}
      onMouseEnter={e => { if (!isActive && !disabled) { e.currentTarget.style.color = 'var(--ink-text)'; } }}
      onMouseLeave={e => { if (!isActive && !disabled) { e.currentTarget.style.color = 'var(--ink-muted)'; } }}
    >
      <Icon className="w-[18px] h-[18px] shrink-0" style={isActive ? { color: 'var(--accent)' } : undefined} strokeWidth={1.8} />
      {!minimized && <span className="flex-grow truncate">{label}</span>}
      {isActive && (
        <span aria-hidden="true" className="absolute -right-3 top-2 w-px h-6" style={{ background: 'var(--accent)' }} />
      )}
    </Link>
    {minimized && (
      <div
        className="absolute left-1/2 -translate-x-1/2 top-full mt-2 px-2.5 py-1 text-xs font-medium rounded-md shadow-lg whitespace-nowrap opacity-0 pointer-events-none group-hover:opacity-100 transition-opacity duration-150 z-50 border"
        style={{ background: 'var(--surf-raised)', color: 'var(--ink-text)', borderColor: 'var(--surf-line)' }}
      >
        {label}
      </div>
    )}
  </div>
);

const Sidebar = () => {
  const location = useLocation();
  const { user, pageAccess } = useAuth();
  const { sidebarMinimized, setSidebarMinimized } = useStore();
  const isSuperadmin = (user?.roles || []).includes('Superadmin');
  const minimized = sidebarMinimized;
  const [collapsed, setCollapsed] = useState(() => {
    const state = {};
    menuSections.forEach((s) => {
      state[s.id] = loadCollapseState(s.id);
    });
    state['platform'] = loadCollapseState('platform');
    return state;
  });

  const toggleSection = useCallback((sectionId) => {
    setCollapsed((prev) => {
      const next = !prev[sectionId];
      saveCollapseState(sectionId, next);
      return { ...prev, [sectionId]: next };
    });
  }, []);

  return (
    <>
      {/* Mobile backdrop — tap outside to close */}
      {!minimized && (
        <div className="fixed inset-0 bg-black/50 z-20 md:hidden" onClick={setSidebarMinimized} aria-hidden="true" />
      )}
      <aside
        className={`${minimized ? '-translate-x-full md:translate-x-0 md:w-16 w-60' : 'translate-x-0 w-60'} flex flex-col fixed left-0 top-16 bottom-0 z-30 transition-all duration-300 ease-in-out overflow-hidden border-r`}
        style={{ background: 'var(--surf-surface)', borderColor: 'var(--surf-line)' }}
        aria-expanded={!minimized}
      >
        {!minimized && (
          <div className="px-3 pt-3">
            <OrgSwitcher />
          </div>
        )}

        {/* Navigation */}
        <nav className="flex-1 px-3 py-3 overflow-y-auto" role="navigation" aria-label="Main navigation">
          {menuSections.map((section) => (
            <div key={section.id} className="mb-1">
              {!minimized && (
                <button
                  onClick={() => toggleSection(section.id)}
                  className="w-full flex items-center justify-between px-1 py-2 mt-2 font-mono text-[10.5px] tracking-[0.12em] transition-colors"
                  style={{ color: 'var(--ink-faint)' }}
                >
                  <span>{section.title.toUpperCase()}</span>
                  {collapsed[section.id] ? <ChevronRight className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                </button>
              )}
              {!collapsed[section.id] && (
                <div className="space-y-1">
                  {section.items.map((item) => {
                    const requiredPerm = PATH_PERMISSION[item.path];
                    const hasPageAccess = !requiredPerm || (pageAccess || []).includes(item.path) || !user?.tenant_id;
                    return (
                      <NavLink
                        key={item.path}
                        to={item.path}
                        icon={item.icon}
                        label={item.label}
                        isActive={location.pathname === item.path}
                        disabled={!hasPageAccess}
                        minimized={minimized}
                      />
                    );
                  })}
                </div>
              )}
            </div>
          ))}

          {/* Platform section — Superadmin only */}
          {isSuperadmin && (
            <div className="mb-1">
              {!minimized && (
                <button
                  onClick={() => toggleSection('platform')}
                  className="w-full flex items-center justify-between px-1 py-2 mt-2 font-mono text-[10.5px] tracking-[0.12em] transition-colors"
                  style={{ color: 'var(--ink-faint)' }}
                >
                  <span>PLATFORM</span>
                  {collapsed['platform'] ? <ChevronRight className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                </button>
              )}
              {!collapsed['platform'] && (
                <div className="space-y-1">
                  <NavLink
                    to="/platform/organizations"
                    icon={Globe}
                    label="Organizations"
                    isActive={location.pathname === '/platform/organizations'}
                    minimized={minimized}
                  />
                </div>
              )}
            </div>
          )}
        </nav>

        {/* Collapse + version */}
        <div className="px-3 pb-3 pt-2 border-t" style={{ borderColor: 'var(--surf-line)' }}>
          <button
            type="button"
            onClick={setSidebarMinimized}
            className={`w-full flex items-center h-10 rounded-lg text-sm transition-colors ${minimized ? 'justify-center px-0' : 'gap-3 px-3'}`}
            style={{ color: 'var(--ink-muted)' }}
            aria-label={minimized ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {minimized ? <PanelLeftOpen className="w-[18px] h-[18px]" strokeWidth={1.8} /> : <PanelLeftClose className="w-[18px] h-[18px]" strokeWidth={1.8} />}
            {!minimized && <span>Collapse sidebar</span>}
          </button>
          {!minimized && (
            <span className="block font-mono text-[11px] px-3 pt-1" style={{ color: 'var(--ink-faint)' }}>v[VERSION]</span>
          )}
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
